import React, { useState } from "react";
import "./../elementPages.less";
import dayjs from "dayjs";
import { Button, Tooltip, Select } from "antd";
import { descMap } from "@/constants/desc";
import { formatBySig, resolveSig, formatErrorPercent, isNumValue } from "@/utils/numberFormat";

interface StreamResultProps {
  source: {
    inputStreams: any[];
    outStreams: any[];
  };
  selectTreeButton?: string;
  selectTreeButtonType?: string;
  input?: any;
  compareItems?: any[];
  streamDescMap?: Record<string, string>;
  streamNodeMap?: Record<string, { fromName: string; toName: string }>;
  elementDescMap?: Record<string, string>;
  tempUnit?: string;
  pressUnit?: string;
}

const phaseMap: Record<string, string> = {
  V: "气相",
  L: "液相",
  VL: "气液混合",
};

const temperatureUnits = ["℃", "K"];
const pressureUnits = ["Pa", "bar", "KPa", "MPa", "atm"];

export default function StreamsDataTable({ source, selectTreeButton, selectTreeButtonType, input, compareItems = [], streamDescMap = {}, streamNodeMap = {}, elementDescMap = {}, tempUnit, pressUnit }: StreamResultProps) {
  const [internalTemperatureUnit, setInternalTemperatureUnit] = useState("℃");
  const [internalPressureUnit, setInternalPressureUnit] = useState("MPa");
  // 液相体积流量单位切换（m³/h / L/h）
  const [selectVolFlow, setSelectVolFlow] = useState("volFlowM3h");
  const temperatureUnit = tempUnit ?? internalTemperatureUnit;
  const pressureUnit = pressUnit ?? internalPressureUnit;
  const [showAllData, setShowAllData] = useState(true);
  console.log("source", source);

  // 获取输出流股的 rule 映射（用于 Flash2 闪蒸罐标识顶部/底部）
  const getStreamRuleMap = () => {
    const ruleMap: Record<string, string> = {};
    if (selectTreeButtonType === "Flash2" && input?.inputs?.elements) {
      const element = input.inputs.elements.find((e: any) => e.name === selectTreeButton);
      if (element?.outputStreams) {
        element.outputStreams.forEach((s: any) => {
          ruleMap[s.name] = s.rule;
        });
      }
    }
    return ruleMap;
  };
  const streamRuleMap = getStreamRuleMap();

  // 数值展示：有效数字格式化（非 0 值不会显示成 0，极小/极大值用科学计数法），sig 由字段内自适应精度决定
  const safeToFixed = (value: any, sig: number = 6): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return "-";
    }
    return formatBySig(value, sig);
  };

  // 计算误差百分比
  const calculateErrorPercent = (compareValue: number, calcValue: number): string => {
    // 判断设计值/实际值/任务值和计算值是否都为 0
    if (compareValue === 0 && calcValue === 0) {
      return "0%";
    }
    // 如果计算值不为 0 但设计值/实际值/任务值为 0
    if (compareValue === 0 && calcValue !== 0) {
      return "100%";
    }
    // 正常计算误差百分比：(计算值 - 设计值/实际值/任务值) ÷ 设计值/实际值/任务值
    const errorPercent = ((calcValue - compareValue) / compareValue) * 100;
    //非 0 但极小的误差显示 <0.01%，避免出现"0.00% 却被标红"的矛盾
    return formatErrorPercent(errorPercent);
  };

  // 判断当前值与对比值是否有差异（用于整行标红，与输入参数对比高亮方式一致）
  const valuesDiffer = (current: any, compare: any): boolean => {
    // 缺失值：- / null / undefined
    const curMissing = current === "-" || current === null || current === undefined;
    const cmpMissing = compare === "-" || compare === null || compare === undefined;
    // 任一侧缺失（页面显示为 "-"）都不算差异，不标红
    if (curMissing || cmpMissing) return false;
    const curStr = String(current);
    const cmpStr = String(compare);
    const curNum = parseFloat(curStr);
    const cmpNum = parseFloat(cmpStr);
    const curIsNum = !isNaN(curNum);
    const cmpIsNum = !isNaN(cmpNum);
    if (curIsNum && cmpIsNum) {
      // 数值比较：相对容差比较，忽略浮点噪声（相对误差 < 0.0000001% 视为相等，与显示一致）
      if (curNum === cmpNum) return false;
      const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
      return Math.abs(curNum - cmpNum) > tolerance;
    }
    // 非数值：字符串直接比较
    return curStr !== cmpStr;
  };

  // 从 input 中提取含 MW 的材料数据，用于计算平均摩尔质量
  const inputMaterials = input?.inputs?.streams?.find((s: any) => s.materials?.length > 0)?.materials || [];

  // 🔴 适配新结构：item.values.mix_input.streams
  const getCompareStreams = (streamName: string) => {
    return compareItems.map((item) => ({
      ...item,
      // 优先用各页面统一上报的输出换算数据（fullOutput），否则回退 mix_input
      stream: item?.values?.fullOutput?.streams?.find((s: any) => s.name === streamName)
        || item?.values?.mix_input?.streams?.find((s: any) => s.name === streamName),
    }));
  };

  // 🔴 计算流股的平均摩尔质量，用于反算摩尔流量
  const calcAvgMW = (materials: any[], massFlow: number, molarFracs: number[]) => {
    if (!materials || !molarFracs || molarFracs.length !== materials.length) return null;
    let totalMolarFrac = 0;
    let totalMassFrac = 0;
    materials.forEach((mat, idx) => {
      const frac = molarFracs[idx] || 0;
      totalMolarFrac += frac;
      totalMassFrac += frac * mat.MW;
    });
    if (totalMolarFrac === 0) return null;
    return totalMassFrac / totalMolarFrac;
  };

  // 生成表头（支持多对比数据）
  const renderHeaders = (visibleFields: string[]) => {
    const fieldLabelMap: Record<string, string> = {
      T: `温度(${temperatureUnit})`,
      P: `压力(${pressureUnit})`,
      f_mol_per_h: "摩尔流量（kmol/h）",
      f_kg_per_h: "质量流量（kg/h）",
      f_nm3_per_h: "气相体积流量（Nm³/h）",
      volFlowLiquid: "液相体积流量",
      vf: "气化率",
      H: "焓（J/mol）",
    };
    const compareCount = compareItems.length;
    const colSpanPerParam = 1 + compareCount;

    const mainHeaders = [
      <th key="stream" rowSpan={2} className="table-header-cell stream-col">流股</th>,
      <th key="desc" rowSpan={2} className="table-header-cell">流股描述</th>,
      <th key="src" rowSpan={2} className="table-header-cell">源</th>,
      <th key="tgt" rowSpan={2} className="table-header-cell">目标</th>,
      <th key="phase" rowSpan={2} className="table-header-cell">相态</th>,
    ];

    visibleFields.forEach((key) => {
      mainHeaders.push(
        <th key={key} colSpan={colSpanPerParam} className="table-header-cell">
          {key === "volFlowLiquid" ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              {fieldLabelMap[key]}
              <Select
                size="small"
                value={selectVolFlow}
                onChange={(v) => setSelectVolFlow(v)}
                style={{ width: 70,marginLeft:10 }}
                options={[
                  { value: "volFlowM3h", label: "m³/h" },
                  { value: "volFlowLh", label: "L/h" },
                ]}
              />
            </span>
          ) : (
            fieldLabelMap[key]
          )}
        </th>
      );
    });

    const subHeaders = [];
    if (compareCount > 0) {
      visibleFields.forEach((key) => {
        subHeaders.push(<th key={`calc-${key}`} className="table-header-cell sub-header">当前计算值</th>);
        compareItems.forEach((item, idx) => {
          subHeaders.push(
            <th key={`compare-${idx}-${key}`} className="table-header-cell sub-header">
              {item.type === "task" 
                ? item.values.name 
                : item.type === "otherCalc"
                ? `${item.values.name} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}
            </th>
          );
        });
      });
    }

    return (
      <>
        <tr className="table-header">{mainHeaders}</tr>
        {compareCount > 0 && <tr className="table-header">{subHeaders}</tr>}
      </>
    );
  };

  // 生成行数据（支持多对比数据）
  const renderRow = (stream: any, visibleFields: string[], isInput: boolean) => {
    const compareStreams = getCompareStreams(stream.name);
    const phase = phaseMap[stream.phase] || stream.phase || "N/A";
    
    // 流股名后标注输入/输出（Flash2 输出流股按 rule 标注顶部/底部）
    let streamDisplayName = stream.name;
    if (selectTreeButtonType === "Flash2" && streamRuleMap[stream.name]) {
      const rule = streamRuleMap[stream.name];
      if (rule === "D") {
        streamDisplayName = `${stream.name}（输出顶部）`;
      } else if (rule === "B") {
        streamDisplayName = `${stream.name}（输出底部）`;
      }
    } else {
      streamDisplayName = `${stream.name}（${isInput ? "输入" : "输出"}）`;
    }
    
    const rowCells = [
      <td key="name" className="table-cell stream-col">{streamDisplayName}</td>,
      <td key="desc" className="table-cell">{streamDescMap[stream.name] || "—"}</td>,
      <td key="src" className="table-cell">
        <Tooltip title={elementDescMap[streamNodeMap[stream.name]?.fromName] || "暂无描述"}>
          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.name]?.fromName || "—"}</span>
        </Tooltip>
      </td>,
      <td key="tgt" className="table-cell">
        <Tooltip title={elementDescMap[streamNodeMap[stream.name]?.toName] || "暂无描述"}>
          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.name]?.toName || "—"}</span>
        </Tooltip>
      </td>,
      <td key="phase" className="table-cell">{phase}</td>,
    ];

    // 计算值
    const calcData = {
      T: temperatureUnit === "℃" ? stream.T_C : stream.T_K,
      P: {
        Pa: stream.P_Pa,
        bar: stream.P_bar,
        KPa: stream.P_KPa,
        MPa: stream.P_MPa,
        atm: stream.P_atm,
      }[pressureUnit] || 0,
      f_mol_per_h: stream.f_mol_per_h/1000,
      f_kg_per_h: stream.f_kg_per_h,
      f_nm3_per_h: stream.f_nm3_per_h,
      volFlowLiquid: selectVolFlow === "volFlowM3h" ? stream.f_m3_per_h : stream.f_l_per_h,
      vf: stream.vf,
      H: stream.H,
    };

    visibleFields.forEach((key) => {
      // 先计算该字段所有对比值的原始值，并判断是否有差异
      const compareCells = compareStreams.map((cs) => {
        if (!cs.stream?.params) {
          // 对比值缺失（显示为 "-"）：不算差异，不标红
          return { value: null as any, errorPercent: "-", diff: valuesDiffer(calcData[key], null) };
        }
        let designValue = 0;
        const params = cs.stream.params;
        const massFlow = params.f_unit === "kg/h" ? params.f : null;
        // 直接用对比流股的 f_mol_per_h，不需要通过 massFlow/avgMW 反推
        const molarFlow = (cs.stream.f_mol_per_h > 0) ? cs.stream.f_mol_per_h / 1000 : null; // mol/h → kmol/h

        switch (key) {
          case "T":
            // 适配温度单位：params.t_unit 可能为 C/K
            designValue = params.t_unit === "C" ? params.t : params.t - 273.15;
            if (temperatureUnit === "K") {
              designValue = params.t_unit === "K" ? params.t : params.t + 273.15;
            }
            break;
          case "P":
            // 适配压力单位：params.p_unit 可能为 MPa；缺失（null）时保留 null，显示为 "-"
            designValue = params.p ?? null;
            if (designValue === null) break;
            if (pressureUnit === "Pa") designValue = params.p * 1e6;
            if (pressureUnit === "KPa") designValue = params.p * 1000;
            if (pressureUnit === "bar") designValue = params.p * 10;
            if (pressureUnit === "atm") designValue = params.p / 0.101325;
            break;
          case "f_mol_per_h":
            designValue = molarFlow;
            break;
          case "f_kg_per_h":
            designValue = massFlow;
            break;
          case "f_nm3_per_h":
            designValue = cs.stream?.f_nm3_per_h ?? null;
            break;
          case "volFlowLiquid":
            designValue = selectVolFlow === "volFlowM3h"
              ? (cs.stream?.f_m3_per_h ?? null)
              : (cs.stream?.f_l_per_h ?? null);
            break;
          case "vf":
            // 对比数据气化率
            designValue = cs.stream?.vf ?? null;
            break;
          case "H":
            // 对比数据焓值
            designValue = cs.stream?.H ?? null;
            break;
        }
        const showDash = designValue === null || designValue === undefined;
        // 计算误差百分比
        const errorPercent = !showDash && isNumValue(calcData[key])
          ? calculateErrorPercent(designValue, calcData[key])
          : "-";
        // 缺失值（显示为 "-"）不算差异，不标红；两边都有值时才比较
        return {
          value: designValue,
          errorPercent,
          diff: valuesDiffer(calcData[key], designValue),
        };
      });
      // 该字段下任一对比值与计算值有差异，则该字段的计算值+所有对比值单元格标红
      const fieldHasDiff = compareCells.some((c) => c.diff);
      // 该字段内自适应精度：常态 6 位有效数字，有差异时提升精度，保证差异在显示上也能看出来
      const sig = resolveSig([calcData[key], ...compareCells.map((c) => c.value)], valuesDiffer);
      const calcVal = calcData[key];
      rowCells.push(
        <td key={`calc-${key}`} className={`table-cell ${fieldHasDiff ? "compare-cell-diff" : ""}`} title={String(calcVal)}>
          {safeToFixed(calcVal, sig)}
        </td>
      );
      compareCells.forEach((c, cIdx) => {
        rowCells.push(
          <td key={`${compareStreams[cIdx].id}-${key}`} className={`table-cell ${fieldHasDiff ? "compare-cell-diff" : ""}`} title={String(c.value)}>
            {c.value === null || c.value === undefined ? "-" : `${safeToFixed(c.value, sig)}(${c.errorPercent})`}
          </td>
        );
      });
    });

    return <tr key={`${stream.name}-${isInput ? "in" : "out"}`} className="table-row">{rowCells}</tr>;
  };

  // ========== 行列过滤逻辑 ==========
  const fieldKeys = ["T", "P", "f_mol_per_h", "f_kg_per_h", "f_nm3_per_h", "volFlowLiquid", "vf", "H"];

  // 获取某个 stream 某个 field 的计算值
  const getCalcVal = (stream: any, fieldKey: string): any => {
    switch (fieldKey) {
      case "T": return temperatureUnit === "℃" ? stream.T_C : stream.T_K;
      case "P": return ({
        Pa: stream.P_Pa,
        bar: stream.P_bar,
        KPa: stream.P_KPa,
        MPa: stream.P_MPa,
        atm: stream.P_atm,
      } as any)[pressureUnit] || 0;
      case "f_mol_per_h": return stream.f_mol_per_h / 1000;
      case "f_kg_per_h": return stream.f_kg_per_h;
      case "f_nm3_per_h": return stream.f_nm3_per_h;
      case "volFlowLiquid": return selectVolFlow === "volFlowM3h" ? stream.f_m3_per_h : stream.f_l_per_h;
      case "vf": return stream.vf;
      case "H": return stream.H;
      default: return 0;
    }
  };

  // 获取某个 stream 某个 field 的某个对比值
  const getCompareVal = (stream: any, fieldKey: string, compareItem: any): any => {
    const cs = compareItem?.values?.mix_input?.streams?.find((s: any) => s.name === stream.name);
    if (!cs?.params) return null;
    const params = cs.params;
    const massFlow = params.f_unit === "kg/h" ? params.f : null;
    const molarFracs = cs.materials?.map((m: any) => m.molar_frac) || [];
    const avgMW = calcAvgMW(cs.materials || [], massFlow || 0, molarFracs);
    const molarFlow = avgMW && massFlow ? massFlow / avgMW : null;

    switch (fieldKey) {
      case "T": {
        if (temperatureUnit === "K") return params.t_unit === "K" ? params.t : params.t + 273.15;
        return params.t_unit === "C" ? params.t : params.t - 273.15;
      }
      case "P": {
        // 缺失（null）时返回 null，显示为 "-"
        if (params.p === null || params.p === undefined) return null;
        if (pressureUnit === "Pa") return params.p * 1e6;
        if (pressureUnit === "KPa") return params.p * 1000;
        if (pressureUnit === "bar") return params.p * 10;
        if (pressureUnit === "atm") return params.p / 0.101325;
        return params.p;
      }
      case "f_mol_per_h": return molarFlow;
      case "f_kg_per_h": return massFlow;
      case "f_nm3_per_h": return cs?.f_nm3_per_h ?? null;
      case "volFlowLiquid": return selectVolFlow === "volFlowM3h" ? (cs?.f_m3_per_h ?? null) : (cs?.f_l_per_h ?? null);
      case "vf": return cs?.vf ?? null;
      case "H": return cs?.H ?? null;
      default: return null;
    }
  };

  const isZeroOrInvalid = (val: any): boolean => {
    if (val === null || val === undefined || val === "-") return true;
    return Number(val) === 0;
  };

  // 对给定的 streams 数组进行行列过滤
  const filterStreams = (streams: any[]) => {
    if (!streams.length) return { visibleStreams: streams, visibleFields: fieldKeys.slice() };
    if (showAllData) return { visibleStreams: streams, visibleFields: fieldKeys.slice() };

    // 过滤行
    const visibleStreams = streams.filter((stream) => {
      return !fieldKeys.every((fieldKey) => {
        const cv = getCalcVal(stream, fieldKey);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareItems.length; i++) {
          const cv2 = getCompareVal(stream, fieldKey, compareItems[i]);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    // 过滤列
    const visibleFields = fieldKeys.filter((fieldKey) => {
      return !streams.every((stream) => {
        const cv = getCalcVal(stream, fieldKey);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareItems.length; i++) {
          const cv2 = getCompareVal(stream, fieldKey, compareItems[i]);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    return { visibleStreams, visibleFields };
  };

  return (
    <div className="stream-result-container">
      {/* {!tempUnit && (
      <div className="unit-selector-container">
        <div className="unit-selector">
          <span className="unit-selector-label">温度单位切换：</span>
          <div className="unit-buttons">
            {temperatureUnits.map((unit) => (
              <button
                key={unit}
                className={`unit-button ${temperatureUnit === unit ? "active" : ""}`}
                onClick={() => setInternalTemperatureUnit(unit)}
              >
                {unit}
              </button>
            ))}
          </div>
        </div>
        
        <div className="unit-selector pressure-selector">
          <span className="unit-selector-label">压力单位切换：</span>
          <div className="unit-buttons">
            {pressureUnits.map((unit) => (
              <button
                key={unit}
                className={`unit-button ${pressureUnit === unit ? "active" : ""}`}
                onClick={() => setInternalPressureUnit(unit)}
              >
                {unit}
              </button>
            ))}
          </div>
        </div>
      </div>
      )} */}

      {/* 计算过滤后的数据 */}
      {(() => {
        const inputFiltered = filterStreams(source.inputStreams);
        const outputFiltered = filterStreams(source.outStreams);
        const allVisibleFields = Array.from(new Set([...inputFiltered.visibleFields, ...outputFiltered.visibleFields]));
        const compareCount = compareItems.length;
        const totalCols = 5 + allVisibleFields.length * (1 + compareCount);

        return (
          <>
            {/* {compareItems.length > 0 && (
              <div style={{ marginBottom: 12, color: "#1890ff" }}>
                正在对比：{compareItems.map((item) => `${item.type === "design" ? "设计值" : "实际值"} ${item.id}`).join("、")}
              </div>
            )} */}

            <div className="table-container">
              <h3 className="stream-title">流股工艺数据：</h3>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>{renderHeaders(allVisibleFields)}</thead>
                  <tbody>
                    {(() => {
                      // 输入 + 输出合并为一个表格，流股名后标注（输入）/（输出）
                      const mergedStreams = [
                        ...inputFiltered.visibleStreams.map((s: any) => ({ stream: s, isInput: true })),
                        ...outputFiltered.visibleStreams.map((s: any) => ({ stream: s, isInput: false })),
                      ];
                      return mergedStreams.length > 0 ? (
                        mergedStreams.map(({ stream, isInput }) => renderRow(stream, allVisibleFields, isInput))
                      ) : (
                        <tr className="empty-row">
                          <td colSpan={totalCols} className="empty-cell">暂无流股数据</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}